import {
  Card,
  Inline,
  Link,
  Modal,
  type ModalProps,
  omniConfig,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useMemo } from 'react';

import { useManagerUser } from '../../hooks';
import { WrappedMixpanel } from '../../mixpanelWrapper';

const WHAT_TITLE = 'What is it?';
const WHY_TITLE = (integrationName: string) =>
  `Why integrate with ${integrationName}?`;
const HOW_TITLE = 'How to connect?';
const VIEW_FAQS_BUTTON_LABEL = 'View FAQs';

type IntegrationModalProps = {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
  integrationName: string;
  whatDescription: string;
  whyDescription: string;
  steps?: { id: number; text: string }[];
  faqsLink?: string;
  action?: JSX.Element;
};

export const IntegrationWidgetModal = ({
  modalProps,
  integrationName,
  whatDescription,
  whyDescription,
  steps,
  faqsLink,
  action,
}: IntegrationModalProps) => {
  const { userId, companyId } = useManagerUser();

  const mixPanelProperties = useMemo(
    () => ({
      companyId,
      userId,
    }),
    [companyId, userId],
  );

  return (
    <Modal {...modalProps} span={6}>
      <Modal.Header title={integrationName} />

      <Modal.Body>
        <Stack gap="16">
          <Stack>
            <Inline justify="space-between">
              <Typography
                as="h4"
                size="16"
                weight="medium"
                hideParagraphSpacing
              >
                {WHAT_TITLE}
              </Typography>
            </Inline>
            <Typography as="span" size="16" color="subtle">
              {whatDescription}
            </Typography>
          </Stack>
          <Stack>
            <Typography as="h4" size="16" weight="medium" hideParagraphSpacing>
              {WHY_TITLE(integrationName)}
            </Typography>
            <Typography as="span" size="16" color="subtle">
              {whyDescription}
            </Typography>
          </Stack>

          {steps && (
            <Stack>
              <Typography
                as="h4"
                size="16"
                weight="medium"
                hideParagraphSpacing
              >
                {HOW_TITLE}
              </Typography>
              <Card p="16">
                <Stack gap="8">
                  {steps.map((step) => (
                    <Inline align="center" gap="8" key={step.id}>
                      <Inline
                        radius="full"
                        minW={24}
                        minH={24}
                        align="center"
                        justify="center"
                        css={{
                          background: `${omniConfig.theme.colors['background-brand']}`,
                          color: `${omniConfig.theme.colors['icon-brand']}`,
                          fontSize: `${omniConfig.theme.fontSizes[1]}`,
                          fontWeight: `${omniConfig.theme.fontWeights.medium}`,
                        }}
                      >
                        {step.id}
                      </Inline>
                      <Typography as="span" size="14">
                        {step.text}
                      </Typography>
                    </Inline>
                  ))}
                </Stack>
              </Card>
            </Stack>
          )}
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Inline justify="flex-end" align="center">
          {faqsLink && (
            <Link
              size="16"
              icon="ExternalLink"
              onClick={() => {
                WrappedMixpanel.track(
                  `${integrationName} Integration View FAQs`,
                  mixPanelProperties,
                );
                window.open(faqsLink, '_blank');
              }}
            >
              {VIEW_FAQS_BUTTON_LABEL}
            </Link>
          )}
          {action}
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
